import type { Skill } from "@js/types/admin";
import type { User } from "@js/types/auth";
import type { Attachment } from "@js/types/uploads";
import type { IsoDateOrIsoDateTime } from "@js/utils/date/types";

import type {
  Degree,
  FreelancerPublicExternalProfile,
  Location,
  ProfileReviews,
  SavedFilter,
  School,
} from "./common";
import type { Employer } from "./employer";
import type { PaginatedResult } from "./generic";
import type { Job, ResumeAttachment } from "./jobs";
import type { Role } from "./roles";

export type FreelancerState = {
  freelancerProfile: Freelancer | undefined;
  fetchingFreelancerProfile: boolean;

  fetchingJobList: boolean;
  jobList: Array<Job>;

  fetchingJobListSelect: boolean;
  jobSelectList: Array<JobSelect>;

  notInterestedJobIds: number[];
};

export type JobInvitations = PaginatedResult<Job>;

export type JobSelect = {
  id: number;
  title: string;
  employer: Employer;
  invoice_can_be_created: boolean;
};

export type ProfileCompletion = {
  completion_percent: number;
  missing_areas: Array<{
    keywords: EnumType<typeof ENUMS.ProfileCompletionArea>;
    items_count: number | null;
    progress: number;
  }>;
  profile_completion_hit_at: string | null;
};

export type FreelancerForInvoice = {
  company_name: string;
  id: number;
  user: {
    first_name: string;
    has_avatar_set: boolean;
    last_name: string;
    public_name: string;
    email: string;
    avatar_thumbnail: string | null;
    gravatar: string;
  };
};

export type FreelancerPhoneNumber = {
  country_iso2: string;
  id: number;
  phone_number: string;
  phone_number_normalized: string;
};

export type FreelancerForNodeStaff = FreelancerPublic & {
  email: string;
  signed_up_at: string;
  acquisition_source: string;
  utm_source: string;
  utm_campaign: string;
  joining_reasons: Array<string>;
  location_data: {
    ip_check: boolean;
    location_from_ip: string | null;
    reported_address: string | null;
  };
  english_proficiency: string;
  hireflix_interview_link: string | null;
  location_mismatch: boolean;
  average_internal_review_rating: string | null;
  internal_review_ratings_count: number;
  persona_location: string | null;
  idv_status: EnumType<typeof ENUMS.IdentityVerificationStatuses> | null;
  interview_screening_score: string | null;
  interview_screening_admin_url: string | null;
  phone_number: FreelancerPhoneNumber;
};

export function isFreelancerForNodeStaffEntity(
  obj: unknown,
): obj is FreelancerForNodeStaff {
  return (obj as FreelancerForNodeStaff)?.location_mismatch !== undefined;
}

export type FreelancerSuperpower = {
  id: number;
  name: string;
};

export type FreelancerCertificate = {
  id: number;
  certificate: {
    id: number;
    name: string;
    is_default: boolean;
  };
  issuer: string;
  year_of_issuing: number;
  order: number;
};

export type FreelancerSkill = {
  id: number;
  skill: Skill;
  order: number;
  is_superpower: boolean;
};

export type Company = {
  id: number;
  name: string;
  is_default: boolean;
  order: number | null;
};

export type FreelancerWorkExperience = {
  id: number;
  company: Company;
  title: string;
  year_from: number;
  year_to: number | null;
  month_from: number;
  month_to: number | null;
  description: string;
};

export type FreelancerSchool = {
  id: number;
  school: School;
  degree: Degree | null;
  graduation_date: IsoDateOrIsoDateTime | null;
};

export type PutFreelancerWorkExperiencePayload = Array<{
  title: string;
  new_company: string;
  month_from: number;
  month_to: number | null;
  year_from: string;
  year_to: string | null;
  description: string;
  is_currently_working: boolean;
}>;

export type WorkSampleItem = {
  id: number;
  title: string;
  completion_date: IsoDateOrIsoDateTime;
  description: string | null;
  url: string | null;
  main_image: WorkSampleItemImage | null;
  portfolio_item_images: Array<WorkSampleItemImage>;
  order: number;
};

export type PortfolioImage = {
  id: number;
  thumbnail: string;
  attachment: Attachment;
};

type WorkSampleItemImage = {
  id: number;
  image: PortfolioImage;
};

export type FreelancerTotals = {
  total_jobs?: number;
};

export type FreelancerRole = {
  id: number;
  role: Role;
  primary: boolean;
  years_experience: number;
  experience_as_arrow: number;
};

export type FreelancerBase = {
  id: number;
  user: User;
  role: Role | null;
  approved: boolean;
  account: number;
  location: string | null;
  accent_color: string;
  availability_for_work: boolean;
  availability_for_work_options: EnumType<
    typeof ENUMS.FreelancerAvailabilityForWork
  >[];
  working_hours_start: number | null;
  working_hours_end: number | null;
  working_hours_tz_abbr: string | null;
  working_hours_utc_offset: string | null;
};

export type FreelancerPublic = FreelancerBase &
  ProfileReviews & {
    cover_photo: string | null;
    cover_photo_id: number | null;
    freelancer_certificates: Array<FreelancerCertificate>;
    freelancer_skills: Array<FreelancerSkill>;
    freelancer_schools: Array<FreelancerSchool>;
    external_profiles: Array<FreelancerPublicExternalProfile>;
    years_experience: number;
    roles: FreelancerRole[];
    portfolio_items: WorkSampleItem[];
    freelancer_work_experience: Array<FreelancerWorkExperience>;
    can_bid: boolean;
    profile_visits_count: number;
    experience_level?: string | null;
    experience_as_arrow: number;
    resume: ResumeAttachment | null;
  };

export type Freelancer = Omit<
  FreelancerPublic,
  | "cover_photo_id"
  | "cover_photo"
  | "portfolio_items"
  | "freelancer_schools"
  | "experience_as_arrow"
  | "freelancer_certificates"
  | "freelancer_work_experience"
  | "freelancer_skills"
  | "roles"
  | "external_profiles"
  | "location"
> & {
  location_full: Location | null;
  hide_token_balance: boolean;
  show_number_of_jobs: boolean | null;
  profile_visibility: "private" | "public";
  profile_visits_count_visibility: "private" | "public";
  show_availability_coachmark?: boolean;
  has_bid?: boolean;
  company_name?: string;
  pending_approval: boolean;
  created_via_byot: boolean;
  created_via_public_application_flow?: boolean;
  tax_form_type: EnumType<typeof ENUMS.TaxForm> | null;
  idv_status: EnumType<typeof ENUMS.IdentityVerificationStatuses> | null;
  has_ada_embeddings: boolean;
};

export type Talent = FreelancerBase &
  Omit<ProfileReviews, "stars_breakdown"> & {
    average_internal_review_rating: string | null;
    internal_review_ratings_count: number;
    search_score: string;
    matching_skills_percent?: number[];
    personal_rank: number[];
    can_be_invited_to_bid?: boolean;
    is_invited_to_this_job?: boolean;
    timezone: string;
    timezone_offset: string;
    country: string | null;
    location_mismatch: boolean;
    superpowers: Array<FreelancerSuperpower>;
    rate?: number;
  };

export function isTalentEntity(obj: unknown): obj is Talent {
  return (obj as Talent)?.search_score !== undefined;
}

export type SavedTalentFilter = SavedFilter<Record<string, string>>;
